import {
  Button,
  CloseButton,
  Flex,
  Heading,
  IconAddLine,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Modal,
  Pill,
  Spinner,
  Text,
  TextArea,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useOutletContext } from "react-router";
import ActionTypesModal from "../../../../../components/Action/ActionTypesModal";
import { Card } from "../../../../../components/Card";
import {
  ERROR,
  useNotification,
} from "../../../../../components/CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../../../components/DialogYesNo/DialogYesNo";
import { Selector } from "../../../../../components/Selectors/Selector";
import { ActionService } from "../../../../../services/ActionService";
import { formatToReadableTime } from "../../../../../utils/utils";

const ActionCard = ({ action }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { setActions } = useOutletContext();
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function handleRemoveAction() {
    try {
      await ActionService.deleteAction(action.id);
      setActions((prevList) => prevList.filter((a) => a.id !== action.id));
    } catch {
      showNotification(
        t("notifications.general.deleteError", { obj: t("actions.action") }),
        ERROR
      );
    }
  }

  return (
    <Card style={{ border: "1px solid #ccc", padding: "10px" }}>
      <Flex gap="small" wrap="wrap" alignItems="start">
        <Flex.Item shouldGrow>
          <Text as="p" size="small">
            {action.author}
            <br />
            {formatToReadableTime(new Date(action.timestamp + "Z"))}
          </Text>
        </Flex.Item>
        <Flex.Item>
          <Pill color="info">{action.action_type.name}</Pill>
        </Flex.Item>
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("actions.editAction")}
            renderIcon={IconEditLine}
            size="small"
            onClick={() => setOpenEdit(true)}
          />
          <EditActionModal
            open={openEdit}
            setOpen={setOpenEdit}
            action={action}
          />
        </Flex.Item>
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("actions.deleteAction")}
            renderIcon={IconTrashLine}
            size="small"
            onClick={() => setConfirmDelete(true)}
          />
          <DialogYesNo
            message={t("actions.deleteConfirm")}
            openStatus={confirmDelete}
            onClickYes={handleRemoveAction}
            onClickNo={() => setConfirmDelete(false)}
          />
        </Flex.Item>
        <Flex.Item size="100%">
          <Text as="p" size="small">
            {action.text}
          </Text>
        </Flex.Item>
      </Flex>
    </Card>
  );
};

const EditActionModal = ({ open, setOpen, action }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { setActions, actionTypes, setActionTypes } = useOutletContext();
  const [saving, setSaving] = useState(false);
  const [actionText, setActionText] = useState(action.text);
  const [actionType, setActionType] = useState(action.action_type);
  const [addActionTypeOpen, setActionTypeOpen] = useState(false);

  async function handleSave() {
    try {
      setSaving(true);
      const updatedAction = await ActionService.updateAction({
        ...action,
        text: actionText,
        action_type: actionType,
      });

      setActions((prevList) => [
        updatedAction,
        ...prevList.filter((item) => item.id !== updatedAction.id),
      ]);

      setOpen(false);
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("notifications.action.obj"),
        }),
        ERROR
      );
    } finally {
      setSaving(false);
    }
  }

  function handleActionTypeSelect(actionTypeId) {
    setActionType(actionTypes.find((a) => a.id === actionTypeId));
  }

  function handleActionTextChange(e) {
    setActionText(e.target.value);
  }

  const updateActionTypes = (actionTypeList) => {
    setActionTypes(
      actionTypeList.map((actionType) => ({
        id: actionType.id,
        label: actionType.name,
      }))
    );
  };

  return (
    <Modal
      open={open}
      label={t("actions.editAction")}
      onExited={() => {
        setActionText(action.text);
        setActionType(action.action_type);
      }}
      size="medium"
    >
      <Modal.Header>
        <Flex justifyItems="space-between">
          <Flex.Item>
            <Heading as="h3">{t("actions.editAction")}</Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              offset="small"
              onClick={() => setOpen(false)}
              screenReaderLabel={t("global.close")}
            />
          </Flex.Item>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap" gap="medium">
          <Flex.Item shouldGrow>
            <Selector
              label={t("actions.actionType")}
              id="action-type-select"
              options={actionTypes}
              selectHandler={handleActionTypeSelect}
              selectedItemId={actionType.id}
            />
          </Flex.Item>
          <Flex.Item align="end">
            <Button
              renderIcon={IconAddLine}
              onClick={() => setActionTypeOpen(true)}
            >
              {t("actions.addActionType")}
            </Button>
            <ActionTypesModal
              addTypesModalOpen={addActionTypeOpen}
              setAddTypesModalOpen={setActionTypeOpen}
              handleCloseTypesModal={() => setActionTypeOpen(false)}
              setTypes={updateActionTypes}
            />
          </Flex.Item>
          <Flex.Item size="100%">
            <TextArea
              label={t("actions.action")}
              value={actionText}
              onChange={handleActionTextChange}
            />{" "}
          </Flex.Item>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => setOpen(false)} margin="0 x-small 0 0">
          {t("global.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          type="submit"
          disabled={saving}
        >
          {saving ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.save")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ActionCard;
